import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Car, 
  DollarSign, 
  TrendingUp, 
  Clock, 
  Star,
  Zap,
  Gift,
  ArrowLeft,
  ArrowRight,
  Calculator,
  Calendar,
  Users,
  Sparkles,
  Heart
} from 'lucide-react';
import { Button } from '../components/ui/button';

const Earnings = () => {
  const earningsBreakdown = [
    { label: "Base Fare", value: "$2.50", description: "Every ride starts here" },
    { label: "Per Mile", value: "$0.90-$2.00", description: "Depends on demand" },
    { label: "Per Minute", value: "$0.15", description: "Wait time counts" },
    { label: "Your Cut", value: "85%", description: "Yeah, you read that right" },
  ];

  const bonuses = [
    {
      icon: Zap,
      title: "Rush Hour Boost",
      description: "Earn 1.3x-1.4x during peak times (7-9 AM, 5-7 PM). Everyone's trying to get somewhere.",
      highlight: "Up to 40% more"
    },
    {
      icon: TrendingUp,
      title: "Ride Streaks",
      description: "Stack rides back-to-back for bonus multipliers. Stay in the zone, get paid more.",
      highlight: "Up to 12% bonus"
    },
    {
      icon: Users,
      title: "Refer Your Friends",
      description: "Bring other drivers to Reddi and earn 5% of their fares for 30 days. No limits.",
      highlight: "Unlimited referrals"
    },
    {
      icon: Star,
      title: "Loyalty Perks",
      description: "Hit 50 rides and unlock Silver tier—2% fare bonus plus priority ride assignments.",
      highlight: "Better rides, more $$$"
    }
  ];

  const weeklyExamples = [
    { hours: "10 hours", rides: "~15 rides", earnings: "$180-$250", vibe: "Weekend warrior" },
    { hours: "20 hours", rides: "~30 rides", earnings: "$380-$520", vibe: "Solid side hustle" },
    { hours: "40 hours", rides: "~60 rides", earnings: "$800-$1,100", vibe: "Full-time grind" },
  ];

  return (
    <div className="min-h-screen bg-[#020617]">
      {/* Header */}
      <header className="py-6 px-6 md:px-12 border-b border-slate-800">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-red-600 flex items-center justify-center transform -skew-x-6">
              <Car className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-['Barlow_Condensed'] font-black text-white">REDDI</span>
          </Link>
          <Link to="/">
            <Button variant="ghost" className="text-slate-400 hover:text-white gap-2">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Button>
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="py-20 px-6 md:px-12">
        <div className="max-w-4xl mx-auto text-center">
          <div className="w-20 h-20 bg-green-600/20 rounded-full flex items-center justify-center mx-auto mb-8">
            <DollarSign className="w-10 h-10 text-green-500" />
          </div>
          <h1 className="text-4xl md:text-6xl font-['Barlow_Condensed'] font-black text-white uppercase mb-6">
            Actually Make Money
          </h1>
          <p className="text-xl text-slate-400 max-w-2xl mx-auto mb-4">
            We're not here to take most of your earnings. You keep 85% of every fare, plus all your tips. 
            Drive when you want, cash out when you need.
          </p>
          <p className="text-3xl font-bold text-green-500">
            ~$25/hour average
          </p>
        </div>
      </section>

      {/* The Pitch */}
      <section className="py-12 px-6 md:px-12 bg-red-600">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-2xl md:text-3xl font-['Barlow_Condensed'] font-bold text-white">
            "Other apps take 25-30%. We take 15%. Do the math."
          </p>
          <p className="text-red-100 mt-4">— Literally just basic fairness</p>
        </div>
      </section>

      {/* Fare Breakdown */}
      <section className="py-16 px-6 md:px-12 bg-slate-900/50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-['Barlow_Condensed'] font-bold text-white uppercase mb-4 text-center">
            How Fares Break Down
          </h2>
          <p className="text-slate-400 text-center mb-12">Transparent pricing, no surprises.</p>
          <div className="grid md:grid-cols-4 gap-6">
            {earningsBreakdown.map((item, index) => (
              <div 
                key={index}
                className="bg-slate-800/50 border border-slate-700 p-6 text-center"
              >
                <p className="text-slate-400 text-sm mb-2">{item.label}</p>
                <p className="text-3xl font-bold text-white mb-2">{item.value}</p>
                <p className="text-slate-500 text-xs">{item.description}</p>
              </div>
            ))}
          </div>
          <div className="mt-8 p-6 bg-red-600/10 border border-red-600/30 text-center">
            <p className="text-slate-300">
              <span className="text-red-400 font-semibold">Service Fee:</span> $2.99 per ride 
              (paid by rider—doesn't come out of your pocket)
            </p>
          </div>
        </div>
      </section>

      {/* Bonuses */}
      <section className="py-16 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-4 mb-4 justify-center">
            <Gift className="w-10 h-10 text-amber-500" />
            <h2 className="text-3xl font-['Barlow_Condensed'] font-bold text-white uppercase">
              Stack Your Earnings
            </h2>
          </div>
          <p className="text-slate-400 text-center mb-12">Base fare is just the start. Here's where it gets good.</p>
          <div className="grid md:grid-cols-2 gap-6">
            {bonuses.map((bonus, index) => (
              <div 
                key={index}
                className="bg-slate-800/50 border border-slate-700 p-8 hover:border-amber-500/50 transition-colors"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-amber-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <bonus.icon className="w-6 h-6 text-amber-500" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-lg font-bold text-white">{bonus.title}</h3>
                      <span className="text-amber-400 text-sm font-semibold">{bonus.highlight}</span>
                    </div>
                    <p className="text-slate-400">{bonus.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Weekly Earnings Examples */}
      <section className="py-16 px-6 md:px-12 bg-slate-900/50">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-4 mb-4 justify-center">
            <Calculator className="w-10 h-10 text-red-500" />
            <h2 className="text-3xl font-['Barlow_Condensed'] font-bold text-white uppercase">
              Real Numbers
            </h2>
          </div>
          <p className="text-slate-400 text-center mb-12">
            Here's what drivers typically make at different commitment levels.
          </p>
          <div className="space-y-4">
            {weeklyExamples.map((example, index) => (
              <div 
                key={index}
                className="flex items-center justify-between p-6 bg-slate-800/50 border border-slate-700"
              >
                <div className="flex items-center gap-8">
                  <div className="flex items-center gap-2">
                    <Clock className="w-5 h-5 text-slate-400" />
                    <span className="text-white">{example.hours}/week</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Car className="w-5 h-5 text-slate-400" />
                    <span className="text-white">{example.rides}</span>
                  </div>
                  <span className="text-slate-500 text-sm hidden md:inline">{example.vibe}</span>
                </div>
                <div className="text-2xl font-bold text-green-500">{example.earnings}</div>
              </div>
            ))}
          </div>
          <p className="text-center text-slate-500 text-sm mt-6">
            * Real earnings vary by location, time, and demand. These are estimates based on typical activity.
          </p>
        </div>
      </section>

      {/* Tips Section */}
      <section className="py-16 px-6 md:px-12">
        <div className="max-w-4xl mx-auto text-center">
          <Heart className="w-12 h-12 text-red-500 mx-auto mb-6" />
          <h2 className="text-3xl font-['Barlow_Condensed'] font-bold text-white uppercase mb-6">
            100% of Tips Are Yours
          </h2>
          <p className="text-slate-400 mb-8 max-w-2xl mx-auto">
            Riders can tip up to 200% of the fare. Every single penny goes straight to you—we don't touch it. 
            Great vibes = great tips. Simple.
          </p>
          <div className="inline-flex items-center gap-2 px-6 py-3 bg-green-600/20 border border-green-600/30 rounded-lg">
            <Sparkles className="w-5 h-5 text-green-500" />
            <span className="text-green-400 font-semibold">Average tip: 15-20% of fare</span>
          </div>
        </div>
      </section>

      {/* Payout Info */}
      <section className="py-16 px-6 md:px-12 bg-slate-900/50">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-4 mb-12 justify-center">
            <Calendar className="w-10 h-10 text-blue-500" />
            <h2 className="text-3xl font-['Barlow_Condensed'] font-bold text-white uppercase">
              Get Paid Fast
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="p-8 bg-slate-800/50 border border-slate-700 text-center">
              <h3 className="text-xl font-bold text-white mb-4">Weekly Deposits</h3>
              <p className="text-slate-400 mb-4">
                Automatic deposits every Tuesday for the previous week. Set it and forget it.
              </p>
              <p className="text-blue-400 font-medium">Free • No minimum</p>
            </div>
            <div className="p-8 bg-slate-800/50 border border-slate-700 text-center">
              <h3 className="text-xl font-bold text-white mb-4">Instant Cashout</h3>
              <p className="text-slate-400 mb-4">
                Need cash now? Cash out anytime, 24/7, straight to your bank. 
              </p>
              <p className="text-blue-400 font-medium">Small fee • Instant transfer</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 md:px-12">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-['Barlow_Condensed'] font-bold text-white uppercase mb-4">
            Ready to Start Earning?
          </h2>
          <p className="text-slate-400 mb-8">
            Sign up takes 5 minutes. Start driving as soon as you're approved.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register?type=driver">
              <Button className="bg-red-600 hover:bg-red-700 text-white px-8 py-6 text-lg gap-2">
                Apply to Drive
                <ArrowRight className="w-5 h-5" />
              </Button>
            </Link>
            <Link to="/driver-requirements">
              <Button variant="outline" className="border-slate-600 text-white hover:bg-slate-800 px-8 py-6 text-lg">
                Check Requirements
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-slate-800">
        <div className="max-w-7xl mx-auto text-center text-slate-500 text-sm">
          © 2025 REDDI. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default Earnings;
